import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import type { InferType } from 'yup';
import { RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import type { ClassPreference } from '@/types/clientsApiTypes';
import {
  CLASS_PREFERENCE_OPTIONS,
  SEAT_PREFERENCES,
  type ClientPreferenceData,
  type CreateClientFormData,
  type SeatPreference,
} from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { useUpdateClientPreferencesMutation } from '../../../hooks/useUpdateClientPreferencesMutation';
import { ModalKeys } from '../../EditSectionModals/types';
import type { SectionSubmitMeta } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { EditAllergiesForm } from '../allergiesSection/EditAllergiesForm';
import type { AllergiesFormValues } from '../allergiesSection/types';

const schema = yup.object({
  allergies: nullableString().defined().max(300, 'Allergies & dietary restrictions must not exceed 300 characters'),
  fly: yup
    .object({
      seat: yup.mixed<SeatPreference>().oneOf(Object.values(SEAT_PREFERENCES)).nullable().default(null),
      preferred_private_aviation_company: nullableString().defined(),
      fly_private_aviation: yup.mixed<'yes' | 'no'>().oneOf(['yes', 'no']).required().nullable().default(null),
      class_preference: yup
        .array()
        .of(
          yup
            .mixed<ClassPreference>()
            .oneOf(CLASS_PREFERENCE_OPTIONS.map((o) => o.key))
            .required()
        )
        .nullable()
        .default(null),
    })
    .nullable()
    .default(null)
    .defined(),
});

type RelatedAllergiesPayload = {
  preference_data: Partial<ClientPreferenceData>;
  clientId: number;
};

function useRelatedAllergiesOnSave() {
  const mutation = useUpdateClientPreferencesMutation();
  const queryClient = useQueryClient();

  return async (payload: RelatedAllergiesPayload) => {
    await mutation.mutateAsync(
      {
        preference_data: payload.preference_data,
        clientId: payload.clientId,
      },
      {
        onSuccess: () => {
          toast.success('Preferences updated successfully');
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
        },
      }
    );
  };
}

function mapClientToValues(client: CreateClientFormData): AllergiesFormValues {
  const pref = client?.primary_passenger?.preference_data;
  const allergies = typeof pref?.allergies === 'string' ? pref.allergies : (pref?.allergies ?? '') || '';
  const flyPrivateAviation = pref?.fly?.fly_private_aviation;
  const seat = pref?.fly?.seat;

  return {
    allergies,
    fly: {
      seat: seat === 'Window' || seat === 'Aisle' ? seat : null,
      preferred_private_aviation_company: pref?.fly?.preferred_private_aviation_company ?? null,
      fly_private_aviation: flyPrivateAviation === 'yes' || flyPrivateAviation === 'no' ? flyPrivateAviation : null,
      class_preference: [],
    },
  };
}

function isDirtyField(value: unknown): boolean {
  if (value === true) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(isDirtyField);
  }

  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(isDirtyField);
  }

  return false;
}

function getDirtyFields(meta?: SectionSubmitMeta): Partial<Record<keyof AllergiesFormValues, unknown>> {
  if (typeof meta?.dirtyFields !== 'object' || meta.dirtyFields === null) {
    return {};
  }

  return meta.dirtyFields as Partial<Record<keyof AllergiesFormValues, unknown>>;
}

function mapValuesToPayload(
  values: InferType<typeof schema>,
  client: CreateClientFormData | null | undefined,
  meta?: SectionSubmitMeta
): RelatedAllergiesPayload {
  const clientId = client?.primary_passenger?.client?.client_id ?? 0;
  const dirtyFields = getDirtyFields(meta);
  const dirtyFly = typeof dirtyFields.fly === 'object' && dirtyFields.fly !== null
    ? (dirtyFields.fly as Partial<Record<keyof NonNullable<AllergiesFormValues['fly']>, unknown>>)
    : {};

  const preference_data: Partial<ClientPreferenceData> = {};
  const fly: Partial<NonNullable<ClientPreferenceData['fly']>> = {};

  if (isDirtyField(dirtyFields.allergies)) {
    preference_data.allergies = values.allergies ?? '';
  }

  if (isDirtyField(dirtyFly.preferred_private_aviation_company)) {
    fly.preferred_private_aviation_company = values.fly?.preferred_private_aviation_company?.trim() || null;
  }

  if (isDirtyField(dirtyFly.fly_private_aviation)) {
    fly.fly_private_aviation = values.fly?.fly_private_aviation ?? 'no';
  }

  if (isDirtyField(dirtyFly.class_preference)) {
    fly.class_preference = values.fly?.class_preference ?? [];
  }

  if (isDirtyField(dirtyFly.seat) && (values.fly?.seat === 'Window' || values.fly?.seat === 'Aisle')) {
    fly.seat = values.fly.seat;
  }

  if (Object.keys(fly).length > 0) {
    preference_data.fly = fly as ClientPreferenceData['fly'];
  }

  return {
    preference_data,
    clientId,
  };
}

const FieldsRelated = () => <EditAllergiesForm isRelated />;

export const relatedAllergiesSection = defineSection<
  (typeof ModalKeys)['related_allergies'],
  CreateClientFormData,
  typeof schema,
  RelatedAllergiesPayload
>({
  key: ModalKeys.related_allergies,
  title: 'Preferences & Allergies',
  subtitle: 'Health-related allergies and dietary considerations to account for during travel planning.',
  schema,
  mapClientToValues,
  mapValuesToPayload,
  useOnSave: useRelatedAllergiesOnSave,
  Fields: FieldsRelated,
});